/* eslint-disable import/no-unresolved */
import { useEffect } from 'react';

import { useHistory } from 'react-router';

import { getKycStatus, userNativeLoginSuccess } from 'actions/user';
import { TAB_CHANGE } from 'actionTypes/appDrawer';
import { tabNames } from 'components/MobileHolding/utils';
import { IS_RN_WEBVIEW } from 'constants/constants';
import { STORAGE_KEYS } from 'constants/enums';
import routes from 'constants/routes';
import { refreshCacheAndReload } from 'helpers/mixpanel-init';
import { addLogsToSentry } from 'helpers/sentry';
import { APP_DRAWER_TAB_ID_MAPPING } from 'reducers/initialstate/appDrawer';
import { useAppDispatch } from 'storeHooks';

import { useBiometrics } from './componentHooks/useBiometrics/useBiometrics.Provider';
import useLocalStorage from './useLocalStorage';
import { useOAuthLogin } from './useOAuthLogin';
import useTheme from './useTheme';

const RN_MESSAGE_EVENT_TYPE = 'RN_MESSAGE_EVENTS';

const EVENT_TYPES = {
  BIOMETRICS_MESSAGES: 'BIOMETRICS_MESSAGES',
  KEYBOARD_HEIGHT: 'KEYBOARD_HEIGHT',
  MOBILE_DEVICE_ID: 'MOBILE_DEVICE_ID',
  OAUTH_SIGNIN_RESPONSE: 'OAUTH_SIGNIN_RESPONSE',
  AUTH_TOKEN: 'AUTH_TOKEN',
  DELETED_ACCOUNT_EXISTS: 'DELETED_ACCOUNT_EXISTS',
  NATIVE_THEME_SET: 'NATIVE_THEME_SET',
  CLEAR_CACHE: 'CLEAR_CACHE',
  NAVIGATE: 'NAVIGATE',
  FAVORITE_UPDATE: 'FAVORITE_UPDATE',
} as const;

const useRNMessageEvents = () => {
  const { onMessageFromApp: onBiometricsMessageFromApp } = useBiometrics();
  const { setOAuthLoginState } = useOAuthLogin();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { setTheme } = useTheme();

  // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-unused-vars
  const [_, setActiveTab] = useLocalStorage(
    STORAGE_KEYS.MOBILE_HOLDING_ACTIVE_TAB,
    tabNames[0]
  );

  // const user = useAppSelector(state => userSelector(state));
  // const loggedUserType = getLoggedUserType(user);

  useEffect(() => {
    const onMessageHandler = (event: MessageEvent) => {
      const handleEvent = async () => {
        if (!IS_RN_WEBVIEW || event?.data?.type !== RN_MESSAGE_EVENT_TYPE) return;

        const { name, data } = event?.data?.payload ?? {};

        switch (name) {
          case EVENT_TYPES.BIOMETRICS_MESSAGES: {
            onBiometricsMessageFromApp(data);
            break;
          }

          case EVENT_TYPES.OAUTH_SIGNIN_RESPONSE: {
            setOAuthLoginState(data);
            break;
          }

          case EVENT_TYPES.AUTH_TOKEN: {
            const userDetails = data?.userData ?? {};
            dispatch(userNativeLoginSuccess(userDetails));
            if (data?.deviceId) {
              localStorage.setItem('mobileDeviceId', data.deviceId);
            }
            break;
          }

          case EVENT_TYPES.CLEAR_CACHE: {
            refreshCacheAndReload('Mobile App');
            break;
          }

          case EVENT_TYPES.FAVORITE_UPDATE: {
            dispatch(getKycStatus());
            break;
          }

          case EVENT_TYPES.DELETED_ACCOUNT_EXISTS: {
            const { email, userId } = data;

            const url = `${routes.recoverDeleteAccount}?e=${encodeURIComponent(
              btoa(email)
            )}&user_id=${userId}`;
            history.push(url);
            break;
          }

          case EVENT_TYPES.NATIVE_THEME_SET: {
            const { theme } = data;
            setTheme(theme, false);
            break;
          }

          case EVENT_TYPES.MOBILE_DEVICE_ID: {
            localStorage.setItem('mobileDeviceId', data.deviceId);
            break;
          }

          case EVENT_TYPES.NAVIGATE: {
            try {
              const pathname = data.route;
              if (
                pathname.includes('/futures/trade') ||
                pathname.includes('/options/trade')
              ) {
                history.push(data.route);
                // @ts-ignore
                dispatch({
                  type: TAB_CHANGE,
                  payload: APP_DRAWER_TAB_ID_MAPPING.TRADE,
                });
              } else if (pathname === '/trade') {
                history.push('/futures/trade');
                // @ts-ignore
                dispatch({
                  type: TAB_CHANGE,
                  payload: APP_DRAWER_TAB_ID_MAPPING.TRADE,
                });
              } else if (pathname.includes('/chart')) {
                history.push('/futures/trade');
                // @ts-ignore
                dispatch({
                  type: TAB_CHANGE,
                  payload: APP_DRAWER_TAB_ID_MAPPING.CHART_ORDERBOOK,
                });
              } else if (pathname.includes('/positions')) {
                history.push('/futures/trade');
                // @ts-ignore
                setActiveTab(tabNames[1]);
                dispatch({
                  type: TAB_CHANGE,
                  payload: APP_DRAWER_TAB_ID_MAPPING.POSITIONS,
                });
              } else if (pathname.includes('/balances')) {
                history.push('/futures/trade');
                setActiveTab(tabNames[0]);
                dispatch({
                  type: TAB_CHANGE,
                  payload: APP_DRAWER_TAB_ID_MAPPING.POSITIONS,
                });
              } else {
                history.push(data.route);
              }
            } catch (error) {
              console.error('Invalid message from RN', error);
            }
            break;
          }

          default: {
            break;
          }
        }
      };

      handleEvent().catch(error => {
        addLogsToSentry(
          {
            error,
          },
          'Error handling RN message event'
        );
      });
    };

    window.addEventListener('message', onMessageHandler);

    return () => {
      window.removeEventListener('message', onMessageHandler);
    };
  }, [
    dispatch,
    history,
    onBiometricsMessageFromApp,
    setActiveTab,
    setOAuthLoginState,
    setTheme,
  ]);
};

export default useRNMessageEvents;
export { RN_MESSAGE_EVENT_TYPE };
